// src/client/screens/borrower/SettingsScreen.tsx
import { useNavigate } from 'react-router-dom';
import { Bell, ChevronLeft, FileText, Shield } from 'lucide-react';
import { useNotificationPrefs } from '@client/hooks/useNotificationPrefs';
import NavBar from '@client/components/NavBar';
import SectionLabel from '@client/components/SectionLabel';

const APP_VERSION = '1.0.0';

const ICON_BTN: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--c-border)',
  width: 38,
  height: 38,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--c-text)',
  cursor: 'pointer',
};

interface ToggleRowProps {
  Icon: React.ElementType;
  label: string;
  sub: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  isLast?: boolean;
}
function ToggleRow({ Icon, label, sub, checked, onChange, isLast }: ToggleRowProps) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      padding: '14px 16px',
      borderBottom: isLast ? 'none' : '1px solid var(--c-border-lt)',
    }}>
      <span style={{
        width: 32, height: 32, borderRadius: 9,
        background: 'var(--c-surface-alt)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon size={16} color="var(--c-text)" />
      </span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, color: 'var(--c-text)', fontWeight: 500 }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--c-text-muted)', marginTop: 2 }}>{sub}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        style={{
          position: 'relative',
          width: 44, height: 26, borderRadius: 13,
          border: 'none',
          background: checked ? 'var(--c-accent)' : 'var(--c-border)',
          cursor: 'pointer',
          flexShrink: 0,
          transition: 'background 0.2s',
        }}
      >
        <span style={{
          position: 'absolute', top: 3,
          left: checked ? 21 : 3,
          width: 20, height: 20, borderRadius: '50%',
          background: '#FFFFFF',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          transition: 'left 0.2s',
        }} />
      </button>
    </div>
  );
}

export default function SettingsScreen() {
  const navigate = useNavigate();
  const { prefs, update: updatePref } = useNotificationPrefs();

  return (
    <div className="app-shell app-shell--with-nav">
      <main className="scroll-area" style={{ padding: 0 }}>
        {/* Header */}
        <div style={{ padding: '14px 20px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button type="button" aria-label="Go back" style={ICON_BTN} onClick={() => navigate(-1)}>
            <ChevronLeft size={18} />
          </button>
          <span style={{ fontSize: 17, fontWeight: 600, color: 'var(--c-text)' }}>
            Settings
          </span>
        </div>

        {/* Notifications */}
        <div style={{ padding: '0 24px' }}>
          <SectionLabel>Notifications</SectionLabel>
        </div>
        <div style={{ padding: '0 24px' }}>
          <div style={{
            background: 'var(--c-surface)',
            border: '1px solid var(--c-border)',
            borderRadius: 18,
            overflow: 'hidden',
          }}>
            <ToggleRow
              Icon={Bell}
              label="Loan reminders"
              sub="SMS alerts 1 and 3 days before repayment"
              checked={prefs.loanReminders}
              onChange={(v) => updatePref('loanReminders', v)}
            />
            <ToggleRow
              Icon={Bell}
              label="Promotions"
              sub="News about features and loan tiers"
              checked={prefs.promotions}
              onChange={(v) => updatePref('promotions', v)}
              isLast
            />
          </div>
        </div>

        {/* Legal */}
        <div style={{ padding: '0 24px' }}>
          <SectionLabel>Legal</SectionLabel>
        </div>
        <div style={{ padding: '0 24px' }}>
          <div style={{
            background: 'var(--c-surface)',
            border: '1px solid var(--c-border)',
            borderRadius: 18,
            overflow: 'hidden',
          }}>
            {[
              { label: 'Terms of service', Icon: FileText },
              { label: 'Privacy policy', Icon: Shield, isLast: true },
            ].map(({ label, Icon, isLast }) => (
              <a
                key={label}
                href="#"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '14px 16px',
                  borderBottom: isLast ? 'none' : '1px solid var(--c-border-lt)',
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <span style={{
                  width: 32, height: 32, borderRadius: 9,
                  background: 'var(--c-surface-alt)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={16} color="var(--c-text)" />
                </span>
                <span style={{ flex: 1, fontSize: 14, color: 'var(--c-text)', fontWeight: 500 }}>{label}</span>
              </a>
            ))}
          </div>
        </div>

        <p style={{
          textAlign: 'center',
          fontSize: 11,
          color: 'var(--c-text-dim)',
          fontFamily: 'var(--ff-mono)',
          margin: '32px 0 16px',
        }}>
          Thandizo v{APP_VERSION}
        </p>
      </main>

      <NavBar />
    </div>
  );
}
